import { FieldDecision } from 'api';

/** A reviewer-selected reference. `selectedText` is for display; only the
 * offsets are sent. */
export type FieldCitationOverride = {
  start: number;
  stop: number;
  selectedText: string;
};

export type FieldDecisionState = {
  isCorrect: boolean | null; // null = undecided
  correctedValue: string | null;
  /** Sent as a flag rather than "": an untouched Incorrect field holds "", which
   * the API rejects as an empty correction. */
  shouldBeBlank: boolean;
  /** On Correct and Incorrect fields alike, several per field. */
  citationOverrides: FieldCitationOverride[];
};

export const EMPTY_DECISION: FieldDecisionState = {
  isCorrect: null,
  correctedValue: null,
  shouldBeBlank: false,
  citationOverrides: [],
};

/** Whether switching to Correct would discard something the reviewer entered,
 * including a Should be blank choice. */
export const hasEnteredCorrection = (decision: FieldDecisionState): boolean =>
  decision.isCorrect === false && (decision.shouldBeBlank || !!decision.correctedValue);

/** The Correct/Incorrect toggle. Switching to Correct discards the correction
 * and the blank choice; references survive either way. */
export const toggleDecision = (
  decision: FieldDecisionState,
  next: 'correct' | 'incorrect'
): Partial<FieldDecisionState> =>
  next === 'correct'
    ? { isCorrect: true, correctedValue: null, shouldBeBlank: false }
    : { isCorrect: false, correctedValue: decision.correctedValue ?? '' };

/** Checking clears the correction and the references (a blank has no text to
 * point at); unchecking re-enables the input, empty. */
export const setShouldBeBlank = (checked: boolean): Partial<FieldDecisionState> =>
  checked
    ? { shouldBeBlank: true, correctedValue: null, citationOverrides: [] }
    : { shouldBeBlank: false, correctedValue: '' };

/** Appends a reference. The same span twice is ignored; the API rejects it. */
export const addReference = (
  references: FieldCitationOverride[],
  reference: FieldCitationOverride
): FieldCitationOverride[] =>
  references.some(r => r.start === reference.start && r.stop === reference.stop)
    ? references
    : [...references, reference];

export const removeReference = (
  references: FieldCitationOverride[],
  index: number
): FieldCitationOverride[] => references.filter((_, i) => i !== index);

/** One decided field's entry in the submit request. */
export const toSubmitDecision = (
  fieldName: string,
  decision: FieldDecisionState
): FieldDecision => {
  const blank = decision.isCorrect === false && decision.shouldBeBlank;
  return {
    fieldName,
    isCorrect: decision.isCorrect,
    correctedValue:
      decision.isCorrect === false && !blank ? (decision.correctedValue ?? null) : null,
    shouldBeBlank: blank,
    citationOverrides:
      decision.isCorrect == null || blank
        ? []
        : decision.citationOverrides.map(({ start, stop }) => ({ start, stop })),
  };
};
